/**
 * PhoneScreenBlock — the confirmation card for a `phone_screen` proposal.
 *
 * The assistant only DRAFTS (backend tool propose_phone_screen). Nothing is
 * dialled until the HR clicks "Start call" here; the card then posts, one
 * request per candidate, the very same JSON the page's VoiceScreenPopup sends
 * to POST /api/voice-screening/call, so plan gating (ai_phone_screening), the
 * super-admin kill switch, the double-dial guard, monthly allowance and the
 * per-call credit charge are identical to a manual call. Candidate emails are
 * looked up from the page's own candidate list (never from the model); phone
 * numbers never reach this UI — the backend dials the number on file.
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, Phone, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/config/api';
import { useCopilot } from './CopilotContext';
import { InitialsAvatar, StatusPill } from './CopilotBlocks';

export const CALL_ENDPOINT = '/api/voice-screening/call';

const DEFAULT_MODES = [
  { key: 'hr', label: 'HR screen', minutes: 5, credits: 1 },
  { key: 'technical', label: 'Technical screen', minutes: 15, credits: 3 },
];

/** Build the JSON body exactly like VoiceScreenPopup does. */
export function buildCallBody({ jobId, email, mode, initiatedBy }) {
  return {
    job_id: jobId,
    candidate_email: String(email || '').toLowerCase(),
    mode: mode === 'technical' ? 'technical' : 'hr',
    initiated_by: initiatedBy || undefined,
  };
}

/**
 * Map one call response to `{ ok, text }` using the same rules as the page's
 * popup. `ok` is true only when a call was actually initiated.
 */
export function callOutcomeMessage(status, body = {}) {
  const b = body && typeof body === 'object' ? body : {};
  if (status >= 200 && status < 300) return { ok: true, text: 'Calling…' };
  if (status === 409) return { ok: false, text: 'Already in progress' };
  if (status === 422) return { ok: false, text: 'No valid mobile number' };
  if (status === 403 && b.upgrade_required) return { ok: false, text: 'Not in your plan' };
  if (status === 403 && b.feature_disabled) return { ok: false, text: 'Switched off by the administrator' };
  if (status === 402) return { ok: false, text: `Not enough credits${b.required ? ` (needs ${b.required})` : ''}` };
  return { ok: false, text: b.error || `Could not start the call (HTTP ${status})` };
}

function readActor() {
  try {
    return (
      window.sessionStorage.getItem('admin_email') ||
      window.localStorage.getItem('admin_email') ||
      window.sessionStorage.getItem('employer_email') ||
      window.localStorage.getItem('employer_email') ||
      null
    );
  } catch {
    return null;
  }
}

function fmtCredits(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

export default function PhoneScreenBlock({ block, readOnly = false, onPrompt }) {
  const copilot = useCopilot();
  const modeOptions = Array.isArray(block.mode_options) && block.mode_options.length ? block.mode_options : DEFAULT_MODES;
  const [mode, setMode] = useState(modeOptions.some((m) => m.key === block.mode) ? block.mode : modeOptions[0].key);
  const [state, setState] = useState('idle'); // idle | busy | done | error | dismissed
  const [results, setResults] = useState([]); // [{ application_id, name, ok, text }]

  const candidates = Array.isArray(block.candidates) ? block.candidates : [];
  const warnings = Array.isArray(block.warnings) ? block.warnings : [];
  const featureEnabled = block.feature_enabled !== false;
  const selectedMode = modeOptions.find((m) => m.key === mode) || modeOptions[0];

  // Emails come from the page's candidate list, keyed by application id.
  const emailById = useMemo(() => {
    const map = new Map();
    (copilot.candidates || []).forEach((c) => {
      const id = c.application_id ?? c.id;
      const email = c.candidate_email || c.email;
      if (id != null && email) map.set(Number(id), email);
    });
    return map;
  }, [copilot.candidates]);

  const withPhone = candidates.filter((c) => c.has_phone !== false);
  const missingEmails = withPhone.filter((c) => !emailById.has(Number(c.application_id)));
  const callable = withPhone.filter((c) => emailById.has(Number(c.application_id)));
  const withLastCall = candidates.filter((c) => c.last_call && typeof c.last_call === 'object');

  const perCall = Number(selectedMode?.credits);
  const estimate = Number.isFinite(perCall) ? perCall * callable.length : block.credits_estimate;
  const estimateText = fmtCredits(estimate);

  const canStart = !readOnly && featureEnabled && (state === 'idle' || state === 'error') && callable.length > 0;

  const modeLabel = (key) => {
    const m = modeOptions.find((o) => o.key === key);
    if (m?.label) return m.label;
    return key === 'technical' ? 'Technical screen' : 'HR screen';
  };

  const handleStart = async () => {
    if (!canStart) return;
    setState('busy');
    setResults([]);
    const actor = readActor();
    const jobId = block.job_id ?? copilot.jobId;
    const outcomes = [];
    // Sequential on purpose: one dial at a time, same as clicking the page's button per candidate.
    for (const c of callable) {
      const body = buildCallBody({ jobId, email: emailById.get(Number(c.application_id)), mode, initiatedBy: actor });
      let outcome;
      try {
        const res = await fetch(`${API_BASE_URL}${CALL_ENDPOINT}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
        let info = {};
        try {
          info = await res.json();
        } catch {
          /* not JSON */
        }
        outcome = callOutcomeMessage(res.status, info);
      } catch {
        outcome = { ok: false, text: 'Could not reach the server' };
      }
      outcomes.push({ application_id: c.application_id, name: c.name, ...outcome });
      setResults([...outcomes]);
    }
    const started = outcomes.filter((o) => o.ok);
    const label = modeLabel(mode);
    if (started.length > 0) {
      setState('done');
      const failed = outcomes.length - started.length;
      toast.success(
        `Started ${started.length} ${label} call${started.length === 1 ? '' : 's'}${failed ? ` · ${failed} not started` : ''}.`
      );
      if (typeof copilot.refreshCandidates === 'function') copilot.refreshCandidates();
      if (onPrompt) {
        const article = /^(HR|[AEIOU])/i.test(label) ? 'an' : 'a';
        onPrompt(`Started ${article} ${label} call for ${started.map((o) => o.name).join(', ')}`, { system: true });
      }
    } else {
      setState('error');
      toast.error(outcomes.length === 1 ? outcomes[0].text : 'No calls were started.');
    }
  };

  const header = (
    <div className="flex items-start gap-2">
      <span className="cp-orb mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center" aria-hidden="true">
        <Phone className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-5 text-foreground">
          AI phone screen
          {selectedMode?.minutes ? <span className="cp-muted ml-1.5 text-[11px] font-normal">· ~{selectedMode.minutes} min</span> : null}
        </div>
        <div className="cp-muted text-[11px] leading-4">
          {readOnly
            ? 'Proposal from an earlier conversation. Ask again to call.'
            : "Nothing is dialled until you confirm. The AI recruiter calls each candidate's number on file."}
        </div>
      </div>
    </div>
  );

  return (
    <div className="cp-card space-y-2.5 p-3" data-testid="phone-screen-card" data-state={state}>
      {header}

      {/* Mode */}
      {!readOnly && (
        <div>
          <div className="cp-label mb-1">Mode</div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Screening mode">
            {modeOptions.map((m) => {
              const active = m.key === mode;
              return (
                <button
                  key={m.key}
                  type="button"
                  className={cn('cp-chip cp-focus h-7 gap-1.5 px-2.5 text-[12px]', active && 'cp-chip-mint font-medium')}
                  aria-pressed={active}
                  onClick={() => setMode(m.key)}
                  disabled={state === 'busy' || state === 'done'}
                  data-testid={`phone-mode-${m.key}`}
                >
                  {active && <Check className="h-3 w-3" aria-hidden="true" />}
                  {m.label || modeLabel(m.key)}
                  <span className="cp-muted cp-num text-[11px] font-normal">
                    {m.minutes ? `~${m.minutes} min` : ''}
                    {m.minutes && m.credits != null ? ' · ' : ''}
                    {m.credits != null ? `${fmtCredits(m.credits)} cr` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {readOnly && <div className="cp-muted text-[12px]">{modeLabel(mode)}</div>}

      {/* Candidates */}
      <div>
        <div className="cp-label mb-1 flex items-center gap-1">
          <Users className="h-3 w-3" aria-hidden="true" /> {candidates.length} candidate{candidates.length === 1 ? '' : 's'}
        </div>
        <ul className="flex flex-wrap gap-1.5">
          {candidates.map((c) => {
            const noPhone = c.has_phone === false;
            return (
              <li
                key={c.application_id}
                className={cn('cp-chip cp-chip-static h-7 gap-1.5 pl-1 pr-2', noPhone && 'opacity-50')}
                title={noPhone ? 'No mobile number on file' : c.status || ''}
                data-testid={`phone-candidate-${c.application_id}`}
                data-callable={noPhone ? 'false' : 'true'}
              >
                <InitialsAvatar name={c.name} size={20} />
                <span className="max-w-[140px] truncate text-[12px]">{c.name}</span>
                {c.status && <StatusPill status={c.status} className="ml-0.5" />}
              </li>
            );
          })}
        </ul>
        {withLastCall.length > 0 && (
          <ul className="cp-muted mt-1 space-y-0.5 text-[11px] leading-4" data-testid="phone-last-calls">
            {withLastCall.map((c) => (
              <li key={c.application_id} className="truncate">
                {c.name} · {modeLabel(c.last_call.mode)} {c.last_call.status || ''}
                {c.last_call.score != null ? <span className="cp-num"> · score {c.last_call.score}</span> : null}
              </li>
            ))}
          </ul>
        )}
        {missingEmails.length > 0 && (
          <div className="cp-banner cp-banner-warn mt-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              {missingEmails.map((c) => c.name).join(', ')} {missingEmails.length === 1 ? 'is' : 'are'} not on this page's list
              right now; reload the applicants and ask again.
            </span>
          </div>
        )}
      </div>

      {/* Warnings */}
      {!featureEnabled && (
        <div className="cp-banner cp-banner-bad" data-testid="phone-feature-off">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>AI phone screening is switched off by the administrator.</span>
        </div>
      )}
      {warnings.length > 0 && (
        <ul className="space-y-1">
          {warnings.map((w, i) => (
            <li key={i} className="cp-banner cp-banner-warn py-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Per-candidate outcomes */}
      {results.length > 0 && (
        <ul className="space-y-0.5 text-[12px]" data-testid="phone-results">
          {results.map((r) => (
            <li key={r.application_id} className="flex items-center gap-1.5">
              {r.ok ? (
                <Phone className="h-3 w-3 shrink-0" style={{ color: 'hsl(var(--cp-ok))' }} aria-hidden="true" />
              ) : (
                <X className="h-3 w-3 shrink-0" style={{ color: 'hsl(var(--cp-bad))' }} aria-hidden="true" />
              )}
              <span className="max-w-[160px] truncate">{r.name}</span>
              <span className="cp-muted truncate">· {r.text}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Footer */}
      {!readOnly && (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
          <div className="cp-muted cp-num text-[11px]">
            {estimateText != null && callable.length > 0
              ? `Estimated ${estimateText} credit${estimateText === '1' ? '' : 's'}`
              : 'Credits as per your plan'}
          </div>
          {state === 'done' ? (
            <span className="cp-pill-ok inline-flex h-8 items-center gap-1 rounded-full px-3 text-[12px] font-medium" data-testid="phone-done">
              <Check className="h-3.5 w-3.5" aria-hidden="true" /> {results.filter((r) => r.ok).length} calling
            </span>
          ) : state === 'dismissed' ? (
            <span className="cp-muted text-[12px]">Dismissed</span>
          ) : (
            <div className="flex items-center gap-1.5">
              <button type="button" className="cp-chip cp-focus h-8" onClick={() => setState('dismissed')} disabled={state === 'busy'}>
                <X className="h-3.5 w-3.5" aria-hidden="true" /> Not now
              </button>
              <button
                type="button"
                className={cn('cp-orb cp-focus inline-flex h-8 items-center gap-1.5 px-3.5 text-[12px] font-semibold', !canStart && 'opacity-50')}
                onClick={handleStart}
                disabled={!canStart}
                data-testid="phone-confirm"
              >
                {state === 'busy' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Phone className="h-3.5 w-3.5" aria-hidden="true" />}
                {state === 'error' ? 'Try again' : `Start ${callable.length === 1 ? '1 call' : `${callable.length} calls`}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
